export interface WaitAndClickArgs {
  selector?: string;
  text?: string;
  buttonLabel?: string;
  withinSelector?: string;
  timeout?: number;
}

export interface WaitAndClickResult {
  success: boolean;
  message: string;
}

const CLICKABLE_SELECTOR =
  "button, a, [role='button'], [onclick], input[type='checkbox'], input[type='radio'], [role='checkbox'], [role='switch']";

function findControlByAriaLabel(container: HTMLElement, label: string): HTMLElement | null {
  const escaped = CSS.escape(label);
  for (const selector of [
    `input[aria-label="${escaped}"]`,
    `[role="checkbox"][aria-label="${escaped}"]`,
    `[role="switch"][aria-label="${escaped}"]`,
  ]) {
    const element = container.querySelector(selector);
    if (element instanceof HTMLElement) {
      return element;
    }
  }
  return null;
}

/** When label text sits beside a toggle/checkbox (not on the control itself), find the control in the same row. */
function findControlNearLabelText(element: HTMLElement): HTMLElement | null {
  const row = element.closest("label, li, fieldset, [role='group'], [role='row']") ?? element.parentElement;
  if (!row) return null;

  const control = row.querySelector(
    "input[type='checkbox'], input[type='radio'], [role='checkbox'], [role='switch']",
  );
  if (control instanceof HTMLElement) {
    return control;
  }

  return null;
}

/**
 * Wait for an element to appear on the page and then click it.
 * This tool can find elements by CSS selector, text content, or button label.
 * It can also scope the search to a specific container using withinSelector.
 */
export async function waitAndClick(args: WaitAndClickArgs): Promise<WaitAndClickResult> {
  const timeout = args.timeout || 5000;
  const startTime = Date.now();

  // Get the container to search within
  const getContainer = (): HTMLElement | null => {
    if (args.withinSelector) {
      const container = document.querySelector(args.withinSelector);
      if (container && container instanceof HTMLElement) {
        return container;
      }
    }
    return document.body;
  };

  // Helper function to find element
  const findElement = (): HTMLElement | null => {
    const container = getContainer();
    if (!container) return null;

    // Try selector first
    if (args.selector) {
      const element = container.querySelector(args.selector);
      if (element && element instanceof HTMLElement) {
        return element;
      }
    }

    const label = args.buttonLabel || args.text;
    if (label) {
      const byAriaLabel = findControlByAriaLabel(container, label);
      if (byAriaLabel) return byAriaLabel;
    }

    // Try button by label
    if (args.buttonLabel) {
      const buttons = Array.from(container.querySelectorAll("button"));
      const button = buttons.find(
        (btn) =>
          btn.textContent?.trim() === args.buttonLabel ||
          btn.getAttribute("aria-label") === args.buttonLabel ||
          btn.getAttribute("name") === args.buttonLabel,
      );
      if (button) return button;
    }

    // Try by text content
    if (args.text) {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let node: Node | null = walker.nextNode();
      while (node) {
        if (node.textContent?.trim() === args.text) {
          const element = node.parentElement;
          if (element && element instanceof HTMLElement) {
            // If it's a button or clickable element, return it
            if (
              element.tagName === "BUTTON" ||
              element.tagName === "A" ||
              element.onclick ||
              element.getAttribute("role") === "button" ||
              element.matches("input[type='checkbox'], input[type='radio']") ||
              element.getAttribute("role") === "checkbox" ||
              element.getAttribute("role") === "switch"
            ) {
              return element;
            }
            // Otherwise, try to find a clickable parent or sibling control (e.g. toggle next to label text)
            const clickable = element.closest(CLICKABLE_SELECTOR);
            if (clickable && clickable instanceof HTMLElement) {
              return clickable;
            }
            const nearbyControl = findControlNearLabelText(element);
            if (nearbyControl) return nearbyControl;
          }
        }
        node = walker.nextNode();
      }
    }

    return null;
  };

  // Wait for element to appear
  while (Date.now() - startTime < timeout) {
    const element = findElement();
    if (element) {
      // Scroll element into view
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Click the element
      element.click();
      return {
        success: true,
        message: `Successfully clicked element${
          args.buttonLabel
            ? ` with label "${args.buttonLabel}"`
            : args.text
              ? ` with text "${args.text}"`
              : args.selector
                ? ` with selector "${args.selector}"`
                : ""
        }`,
      };
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  throw new Error(
    `Element not found after ${timeout}ms${
      args.buttonLabel
        ? ` (looking for button with label "${args.buttonLabel}")`
        : args.text
          ? ` (looking for element with text "${args.text}")`
          : args.selector
            ? ` (looking for selector "${args.selector}")`
            : ""
    }`,
  );
}
